import {
  CheckableAvatarSelectItem,
  CheckableSelectItem,
  CheckableTagSelectItem,
  Select,
  TagSelectSuggestion,
} from '@dixa/lib-shared-habla';
import { convertChannelCasing, PaginatedApiArg } from 'lib-shared-api-utils';
import { Tag } from 'lib-shared-legacy-types';
import { PaginatedApiResponse } from 'lib-shared-types-api';
import type {
  ReadSearchCompanyApiArg,
  ReadSearchCompanyApiResponse,
  ReadSearchQueueApiArg,
  ReadSearchQueueApiResponse,
  ReadSearchTagApiArg,
  ReadSearchTagApiResponse,
  ReadSearchUserApiArg,
  ReadSearchUserApiResponse,
  SearchCompanyHit,
  SearchFilterCondition,
  SearchFilterConditionCustomAttributeText,
  SearchFilterConditionOneToManyAndNullish,
  SearchFilterConditionOneToOne,
  SearchFilterConditionOneToOneAndNullish,
  SearchFilterField,
  SearchQueueHit,
  SearchTagHit,
  SearchUserHit,
} from 'lib-shared-types-api';
import { Channel } from 'lib-shared-types-channels';
import type { LodashMap1x1 } from 'lodash/fp';
import map from 'lodash/fp/map';
import {
  ComponentType,
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { searchApi } from 'apis/search';
import {
  useLazyPaginatedEndpoint,
  UseLazyPaginatedOptions,
  UsePaginatedResult,
} from 'utils/apis/pagination/useLazyPaginatedEndpoint';
import { getChannelFeatureFlagsStatus } from 'utils/featureFlagging/channelFeatureFlags';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

import {
  filterItems,
  FilterItemType,
} from '../../../../../../utils/definitions';
import { ConditionFieldProps } from '../../conditionField';

import { useConditionFieldSelect } from './utils/useConditionFieldSelect';

type SearchHit =
  | SearchQueueHit
  | SearchUserHit
  | SearchTagHit
  | SearchCompanyHit;

type SearchItemFormatter<TSearchHit extends SearchHit> = LodashMap1x1<
  TSearchHit,
  FilterItemType
>;

const defaultItemFormatter = map<
  SearchQueueHit | SearchCompanyHit,
  FilterItemType
>(({ id, name }) => ({ id, name }));

const tagItemFormatter = map<SearchTagHit, FilterItemType>(
  ({ id, name, color }) => ({
    id,
    name,
    color,
  }),
);

const userItemFormatter = map<SearchUserHit, FilterItemType>(
  ({ id, display_name, phone_number, email }) => ({
    id,
    name: display_name || email || phone_number || id,
  }),
);

type CreateFreeTextFilterConfig = {
  readonly emptyMessage: string;
  readonly renderSuggestion: (input: string) => string;
  readonly transformSuggestion: (input: string) => FilterItemType | null;
};

function createFreeTextFilter<TCondition extends SearchFilterCondition>({
  emptyMessage,
  renderSuggestion,
  transformSuggestion,
}: CreateFreeTextFilterConfig): FunctionComponent<
  ConditionFieldProps<TCondition>
> {
  function Filter({
    condition,
    onChange,
    ...props
  }: ConditionFieldProps<TCondition>): JSX.Element {
    const { handleChange, handleClear, mapValuesToItems } =
      useConditionFieldSelect({ condition, onChange });

    const [searchTerm, setSearchTerm] = useState('');
    const selectedItems = mapValuesToItems();

    // When there is a search term, don't render the empty state.
    const emptyComponent = searchTerm ? () => null : undefined;

    /** Handles adding a new free-text value to the selection. */
    const handleAddSuggestion = ({ id }: FilterItemType) => {
      const item = transformSuggestion(id.toString());

      if (item) {
        handleChange(item);
      }
      setSearchTerm('');
    };

    return (
      <Select<FilterItemType>
        multiple
        clearable
        searchable
        placeholder="Type a value and press Enter"
        items={[]}
        selectedItems={selectedItems}
        onChange={handleAddSuggestion}
        onClear={handleClear}
        hideSelectedItems
        limit={3}
        disabled={props.disabled}
        onSearch={setSearchTerm}
        suggestionComponent={TagSelectSuggestion}
        onSuggest={(suggestion: string) =>
          handleAddSuggestion({ id: suggestion, name: suggestion })
        }
        renderSuggestion={renderSuggestion}
        emptyMessage={emptyMessage}
        emptyComponent={emptyComponent}
      />
    );
  }

  return Filter;
}

function createStaticFilter<TCondition extends SearchFilterCondition>(
  field: SearchFilterField,
): FunctionComponent<ConditionFieldProps<TCondition>> {
  function Filter({
    condition,
    onChange,
    ...props
  }: ConditionFieldProps<TCondition>): JSX.Element {
    const { handleChange, handleClear, mapValuesToItems } =
      useConditionFieldSelect({ condition, onChange });

    const flags = useFeatureFlag(['instagram-management']);
    const channelFlags = getChannelFeatureFlagsStatus();

    const items = useMemo(() => {
      return field === 'initial_channel'
        ? filterItems[field].filter(({ id: channel }) => {
            const casedChannel = convertChannelCasing(
              channel as string,
              'snake',
            ) as Channel;
            if (casedChannel === 'facebook_messenger') {
              // Instagram conversations appear as Facebook conversations
              return (
                channelFlags.get(casedChannel) || flags.instagramManagement
              );
            }
            return channelFlags.get(casedChannel);
          })
        : filterItems[field];
    }, [flags, channelFlags]);

    const selectedItems = mapValuesToItems();

    const { placeholder, disabled } = props;

    return (
      <Select
        multiple
        clearable
        searchable
        placeholder={placeholder}
        items={items}
        selectedItems={selectedItems}
        onChange={handleChange}
        onClear={handleClear}
        hideSelectedItems
        limit={3}
        disabled={disabled}
      />
    );
  }

  return Filter;
}

type CreateDynamicFilterConfig<
  TPaginatedApiArg extends PaginatedApiArg,
  TPaginatedApiResponse extends PaginatedApiResponse,
  TSearchHit extends SearchHit,
> = {
  readonly endpoint: {
    readonly options: UseLazyPaginatedOptions<
      TPaginatedApiArg,
      TPaginatedApiResponse,
      TSearchHit
    >;
    readonly defaultArg: Parameters<
      UsePaginatedResult<
        TSearchHit,
        TPaginatedApiArg,
        TPaginatedApiResponse
      >['changeArg']
    >[0];
  };
  readonly itemComponent: ComponentType<typeof CheckableSelectItem>;
  readonly itemFormatter: SearchItemFormatter<TSearchHit>;
  readonly prepareDisabled?: (
    selectedItems: FilterItemType[],
    items: FilterItemType[],
  ) => FilterItemType['id'][];
};

function createDynamicFilter<
  TCondition extends SearchFilterCondition,
  TPaginatedApiArg extends PaginatedApiArg,
  TPaginatedApiResponse extends PaginatedApiResponse,
  TSearchHit extends SearchHit,
>({
  endpoint: { options, defaultArg },
  itemComponent = CheckableSelectItem,
  itemFormatter,
  prepareDisabled,
}: CreateDynamicFilterConfig<
  TPaginatedApiArg,
  TPaginatedApiResponse,
  TSearchHit
>): FunctionComponent<ConditionFieldProps<TCondition>> {
  function Filter({
    condition,
    placeholder,
    disabled,
    onChange,
  }: ConditionFieldProps<TCondition>) {
    const [searchTerm, setSearchTerm] = useState<string>();

    const {
      items: hits,
      arg,
      asyncStatus,
      changeArg,
      hasNextPage,
      nextPage,
    } = useLazyPaginatedEndpoint({ ...options, infinite: true });

    useEffect(() => {
      if (searchTerm !== arg?.searchTerm) {
        changeArg(
          {
            ...defaultArg,
            searchTerm,
            offset: undefined,
            limit: undefined,
          },
          {
            debounceTime: 300,
          },
        );
      }
      // Don't rely on arg as dependency because we change the arg inside.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [changeArg, searchTerm]);

    const { handleChange, handleClear, mapValuesToItems } =
      useConditionFieldSelect<TCondition>({ condition, onChange });

    const handleSearch = useCallback((value?: string) => {
      setSearchTerm(value?.trim());
    }, []);

    const handleToggle = useCallback(
      (isOpen: boolean) => {
        if (isOpen && !hits.length) {
          changeArg(
            {
              ...defaultArg,
              searchTerm,
              offset: undefined,
              limit: undefined,
            },
            { debounceTime: 0 },
          );
        } else if (!isOpen && searchTerm !== '') {
          setSearchTerm('');
        }
      },
      [changeArg, hits.length, searchTerm],
    );

    const items = itemFormatter(hits);
    const selectedItems = useMemo(() => mapValuesToItems(), [mapValuesToItems]);

    return (
      <Select
        multiple
        clearable
        searchable
        placeholder={placeholder}
        onSearch={handleSearch}
        onChange={handleChange}
        onClear={handleClear}
        onToggle={handleToggle}
        pagination={{
          infinite: true,
          nextEnable: hasNextPage,
          previousEnable: false,
          onNext: () => Promise.resolve(nextPage()),
        }}
        items={items}
        selectedItems={selectedItems}
        disabledItems={prepareDisabled?.(selectedItems, items)}
        itemComponent={itemComponent}
        loading={asyncStatus === 'pending'}
        limit={3}
        disabled={disabled}
      />
    );
  }

  return Filter;
}

export const ConditionFieldId =
  createFreeTextFilter<SearchFilterConditionOneToOne>({
    emptyMessage: 'Type the numeric conversation ID (e.g. 123456)',
    renderSuggestion: (input: string) => `Add "#${input.replace('#', '')}"`,
    transformSuggestion: (input: string) => {
      const id = input.replace('#', '');
      const isValid = /^[0-9]*$/.test(id);
      return isValid ? { id, name: `#${id}` } : null;
    },
  });

export const ConditionFieldText =
  createFreeTextFilter<SearchFilterConditionOneToOne>({
    emptyMessage: 'Enter the text to match...',
    renderSuggestion: (input: string) => `Add "${input}"`,
    transformSuggestion: (input: string) => ({ id: input, name: input }),
  });

export const ConditionFieldCustomAttributeText =
  createFreeTextFilter<SearchFilterConditionCustomAttributeText>({
    emptyMessage: 'Enter any custom attribute value (e.g. ID-123)',
    renderSuggestion: (text: string) => `Add "${text}"`,
    transformSuggestion: (input: string) => ({ id: input, name: input }),
  });

export const ConditionFieldDirection =
  createStaticFilter<SearchFilterConditionOneToOne>('initial_direction');
export const ConditionFieldStatus =
  createStaticFilter<SearchFilterConditionOneToOne>('status');
export const ConditionFieldChannel =
  createStaticFilter<SearchFilterConditionOneToOne>('initial_channel');
export const ConditionFieldUserType =
  createStaticFilter<SearchFilterConditionOneToOne>('user_type');

export const ConditionFieldQueue = createDynamicFilter<
  SearchFilterConditionOneToOneAndNullish,
  ReadSearchQueueApiArg,
  ReadSearchQueueApiResponse,
  SearchQueueHit
>({
  endpoint: {
    options: {
      endpoint: searchApi.endpoints.readSearchQueue,
      itemsSelector: ({ data: { results } }) => results.hits ?? [],
    },
    defaultArg: {
      sort: { key: 'name', direction: 'asc' },
    },
  },
  itemComponent: CheckableSelectItem,
  itemFormatter: defaultItemFormatter,
});

export const ConditionFieldContacts = createDynamicFilter<
  SearchFilterConditionOneToOne,
  ReadSearchUserApiArg,
  ReadSearchUserApiResponse,
  SearchUserHit
>({
  endpoint: {
    options: {
      endpoint: searchApi.endpoints.readSearchUser,
      itemsSelector: ({ data: { results } }) => results.hits ?? [],
    },
    defaultArg: {
      sort: { key: 'display_name', direction: 'asc' },
      filter: {
        facets: {
          user_type: ['contact'],
        },
      },
    },
  },
  itemComponent: CheckableAvatarSelectItem,
  itemFormatter: userItemFormatter,
});

export const ConditionFieldAgents = createDynamicFilter<
  SearchFilterConditionOneToOneAndNullish,
  ReadSearchUserApiArg,
  ReadSearchUserApiResponse,
  SearchUserHit
>({
  endpoint: {
    options: {
      endpoint: searchApi.endpoints.readSearchUser,
      itemsSelector: ({ data: { results } }) => results.hits ?? [],
    },
    defaultArg: {
      sort: { key: 'display_name', direction: 'asc' },
      filter: {
        facets: {
          user_type: ['member'],
        },
      },
    },
  },
  itemComponent: CheckableAvatarSelectItem,
  itemFormatter: userItemFormatter,
});

export const ConditionFieldTags = createDynamicFilter<
  SearchFilterConditionOneToManyAndNullish,
  ReadSearchTagApiArg,
  ReadSearchTagApiResponse,
  SearchTagHit
>({
  endpoint: {
    options: {
      endpoint: searchApi.endpoints.readSearchTag,
      itemsSelector: ({ data: { results } }) => results.hits ?? [],
    },
    defaultArg: {
      sort: { key: 'name', direction: 'asc' },
    },
  },
  itemComponent: CheckableTagSelectItem,
  itemFormatter: tagItemFormatter,
  prepareDisabled: (_, tags) =>
    tags.filter((tag) => (tag as Tag).is_deactivated).map((tag) => tag.id),
});

export const ConditionFieldCompany = createDynamicFilter<
  SearchFilterConditionOneToOneAndNullish,
  ReadSearchCompanyApiArg,
  ReadSearchCompanyApiResponse,
  SearchCompanyHit
>({
  endpoint: {
    options: {
      endpoint: searchApi.endpoints.readSearchCompany,
      itemsSelector: ({ data: { results } }) => results.hits ?? [],
    },
    defaultArg: {
      sort: { key: 'name', direction: 'asc' },
    },
  },
  itemComponent: CheckableSelectItem,
  itemFormatter: defaultItemFormatter,
});
